// src/components/Stripe/SuccessPage.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Fade,
  Grow,
  Divider,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';

// Using only the custom confetti component

// Simple custom confetti component as a fallback
const SimpleConfetti = ({ isActive }) => {
  const theme = useTheme();
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    if (!isActive) return;
    
    // Create confetti particles
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
    ];
    
    const newParticles = Array.from({ length: 50 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: -20 - Math.random() * 80,
      size: 5 + Math.random() * 10,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      opacity: 1,
    }));
    
    setParticles(newParticles);
    
    // Clean up
    return () => setParticles([]);
  }, [isActive, theme]);
  
  if (!isActive || particles.length === 0) return null;
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1500,
        overflow: 'hidden',
      }}
    >
      {particles.map((particle) => (
        <Box
          key={particle.id}
          sx={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            borderRadius: '50%',
            transform: `rotate(${particle.rotation}deg)`,
            opacity: particle.opacity,
            animation: 'fall 3s linear forwards',
            '@keyframes fall': {
              to: {
                top: '120%',
                opacity: 0,
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

export default function SuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Get payment details from location state or query params
  const paymentId = location.state?.paymentId || new URLSearchParams(location.search).get('payment_intent');
  const amount = location.state?.amount || '';
  
  useEffect(() => {
    // Simulate checking payment status
    const timer = setTimeout(() => {
      setLoading(false);
      setShowConfetti(true);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh' 
      }}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 500 }}>
          Confirming your payment...
        </Typography>
      </Box>
    );
  }
  

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
      {/* Use the simple confetti version */}
      {showConfetti && (
        <SimpleConfetti isActive={showConfetti} />
      )}
      
      <Fade in={true} timeout={800}>
        <Paper 
          elevation={4} 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          }}
        >
          {/* Header */}
          <Box 
            sx={{ 
              p: 4, 
              pb: 5,
              background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
              color: 'white',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, transparent 60%)',
                zIndex: 0,
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Grow in={true} timeout={1000}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    mx: 'auto',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 60, color: 'white' }} />
                </Box>
              </Grow>
              
              <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
                Payment Successful!
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 2, opacity: 0.9, fontWeight: 400 }}>
                Thank you for your subscription
              </Typography>
              
              {amount && (
                <Chip 
                  label={`Amount: ${amount}`} 
                  sx={{ 
                    mt: 2,
                    fontWeight: 600,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '& .MuiChip-label': {
                      px: 2,
                    }
                  }} 
                />
              )}
            </Box>
          </Box>
          
          {/* Content */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom fontWeight={600} color="success.main">
              Your subscription is now active
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4 }} color="text.secondary">
              You now have full access to all premium features and content
            </Typography>
            
            {paymentId && (
              <Box 
                sx={{ 
                  mb: 4, 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Payment ID:</strong> {paymentId}
                </Typography>
              </Box>
            )}
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              What's Next?
            </Typography>
            
            <Typography variant="body2" paragraph color="text.secondary">
              You can now explore all premium content and features. Visit your dashboard to get started.
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 2, 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                mt: 4,
              }}
            >
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                startIcon={<DashboardIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                Go to Dashboard
              </Button>
              
              <Button 
                variant="outlined"
                size="large"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/subscription')}
                sx={{ 
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                Manage Subscription
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Container>
  );
}
